import { useEffect, useState } from 'react'
import { App, Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'

const { Text } = Typography

interface Row {
  id: string
  log_date: string
  utility_type: string
  equipment_id: string | null
  shift_id: string | null
  running_hours: number | null
  pressure: number | null
  temperature: number | null
  humidity: number | null
  power_consumption_kwh: number | null
  abnormality: boolean
  abnormality_text: string | null
  action_taken: string | null
  status: string
  linked_work_order_id: string | null
}

interface UtilitySummary {
  utility_type: string
  entries: number
  abnormal_entries: number
  total_running_hours: number
  total_power_kwh: number
}

/** Spec §12 — Utility performance report. Abnormal readings highlighted. */
export default function UtilityPerformanceReportPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId,
    from_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
  })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-utility-perf', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.utilityPerformance(filters),
  })
  const rows: Row[] = (data?.rows ?? []) as unknown as Row[]
  const byUtility: UtilitySummary[] = (data?.by_utility ?? []) as unknown as UtilitySummary[]
  const abnormalCount = rows.filter((r) => r.abnormality).length

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/utility-performance', filters,
        `utility-performance-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) { message.error((e as Error).message ?? 'Export failed') }
    finally { setExporting(false) }
  }

  const columns = [
    {
      title: 'Date', dataIndex: 'log_date', key: 'd', width: 110,
      render: (v: string) => dayjs(v).format('DD MMM YYYY'),
    },
    { title: 'Utility', dataIndex: 'utility_type', key: 'u', width: 170 },
    {
      title: 'Status', dataIndex: 'status', key: 's', width: 110,
      render: (v: string) => <Tag color={v === 'OK' ? 'green' : 'red'}>{v}</Tag>,
    },
    {
      title: 'Running h', dataIndex: 'running_hours', key: 'rh', width: 100,
      render: (v: number | null) => v != null ? v.toFixed(1) : '—',
    },
    {
      title: 'Pressure', dataIndex: 'pressure', key: 'pr', width: 100,
      render: (v: number | null) => v != null ? v.toFixed(2) : '—',
    },
    {
      title: 'Temp', dataIndex: 'temperature', key: 'tp', width: 100,
      render: (v: number | null) => v != null ? `${v.toFixed(1)}°` : '—',
    },
    {
      title: 'Humidity', dataIndex: 'humidity', key: 'h', width: 100,
      render: (v: number | null) => v != null ? `${v.toFixed(1)}%` : '—',
    },
    {
      title: 'Power kWh', dataIndex: 'power_consumption_kwh', key: 'pw', width: 110,
      render: (v: number | null) => v != null ? v.toFixed(2) : '—',
    },
    {
      title: 'Abnormality', key: 'ab', width: 220,
      render: (_: unknown, r: Row) => r.abnormality
        ? <Space><Tag color="orange">ABNORMAL</Tag><Text>{r.abnormality_text ?? ''}</Text></Space>
        : null,
    },
    { title: 'Action', dataIndex: 'action_taken', key: 'a', ellipsis: true },
    {
      title: 'Linked WO', dataIndex: 'linked_work_order_id', key: 'lw', width: 130,
      render: (v: string | null) => v ? <Tag>{v.slice(0, 8)}</Tag> : '—',
    },
  ]

  return (
    <div>
      <PageHeader
        title="Utility Performance"
        subtitle="Compressor / Humidification / DG / Panel readings. Abnormalities highlighted." />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ dateRange: true, utilityType: true, abnormalOnly: true }}
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Log entries" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Abnormal entries" value={abnormalCount} valueStyle={{ color: abnormalCount > 0 ? '#fa541c' : undefined }} /></Card></Col>
        <Col xs={24} md={12}>
          <Card size="small" title="By utility">
            <Space wrap>
              {byUtility.map((u) => (
                <Card key={u.utility_type} size="small" style={{ minWidth: 200 }}>
                  <Text strong>{u.utility_type}</Text><br />
                  <Text type="secondary">Entries: {u.entries}</Text><br />
                  <Text type="secondary">Abnormal: {u.abnormal_entries}</Text><br />
                  <Text type="secondary">Run: {u.total_running_hours.toFixed(1)} h · Power: {u.total_power_kwh.toFixed(0)} kWh</Text>
                </Card>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>
      <DataTable rowKey="id" columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
